import React from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import ForceGraph2D from 'react-force-graph-2d';
import {
  AlertTriangle, CheckCircle, AlertCircle, Network, Link2, Maximize2,
} from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '$0';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency', currency: 'ARS',
    minimumFractionDigits: 0, maximumFractionDigits: 0
  }).format(amount);
};

export const InvestigacionRelacionesTab = ({
  relaciones, relationshipGraphData, graphFullscreen, setGraphFullscreen,
}) => (
  <div className="space-y-4">
    {/* Resumen de relaciones */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className={`p-4 ${relaciones.resumen?.hay_transacciones_directas ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
        <div className="flex items-center gap-3">
          {relaciones.resumen?.hay_transacciones_directas ? (
            <AlertTriangle size={24} className="text-red-600" />
          ) : (
            <CheckCircle size={24} className="text-green-600" />
          )}
          <div>
            <p className="font-semibold text-slate-900">Transacciones Directas</p>
            <p className="text-sm text-slate-500">
              {relaciones.resumen?.hay_transacciones_directas
                ? `${relaciones.transacciones_directas?.length || 0} conexiones encontradas`
                : 'No hay transacciones directas entre clientes'}
            </p>
          </div>
        </div>
      </Card>
      <Card className="p-4 bg-white">
        <div className="flex items-center gap-3">
          <Link2 size={24} className="text-amber-600" />
          <div>
            <p className="font-semibold text-slate-900">Contrapartes Comunes</p>
            <p className="text-sm text-slate-500">{relaciones.resumen?.cantidad_contrapartes_comunes || 0} contrapartes en común</p>
          </div>
        </div>
      </Card>
      <Card className={`p-4 ${relaciones.resumen?.riesgo_relacion >= 50 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
        <div className="flex items-center gap-3">
          <div className="text-center">
            <p className={`text-3xl font-bold ${relaciones.resumen?.riesgo_relacion >= 50 ? 'text-red-600' : 'text-amber-600'}`}>
              {relaciones.resumen?.riesgo_relacion || 0}%
            </p>
            <p className="text-xs text-slate-500">Riesgo de Relación</p>
          </div>
        </div>
      </Card>
    </div>

    {/* Grafo de relaciones */}
    <Card className="p-4 bg-white">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
          <Network size={16} />Grafo de Relaciones
        </h3>
        <Button variant="outline" size="sm" onClick={() => setGraphFullscreen(!graphFullscreen)}>
          <Maximize2 size={14} className="mr-1" />{graphFullscreen ? 'Minimizar' : 'Expandir'}
        </Button>
      </div>
      <div style={{ height: graphFullscreen ? 500 : 300 }}>
        {relationshipGraphData.nodes.length > 0 ? (
          <ForceGraph2D
            graphData={relationshipGraphData}
            nodeLabel={(node) => {
              const tipo = node.type === 'investigado' ? 'Cliente Investigado' : 'Contraparte Común';
              const id = node.full_id || node.id || node.label;
              const nombre = node.nombre || node.razon_social || '';
              return nombre ? `${nombre}\n${id}\nTipo: ${tipo}` : `${id}\nTipo: ${tipo}`;
            }}
            nodeColor={(node) => node.color}
            nodeRelSize={10}
            linkColor={(link) => link.color}
            linkWidth={(link) => link.type === 'directa' ? 4 : 2}
            linkDirectionalArrowLength={8}
            linkDirectionalArrowRelPos={0.9}
            linkLabel={(link) => link.type === 'directa'
              ? `Tx Directa: ${link.cantidad || 0} transacciones\nVolumen: $${(link.volumen || 0).toLocaleString()}`
              : 'Conexión común'}
            linkCurvature={0.2}
            backgroundColor="#ffffff"
            cooldownTicks={100}
            d3VelocityDecay={0.3}
            nodeCanvasObject={(node, ctx, globalScale) => {
              const size = node.type === 'investigado' ? 12 : 8;
              ctx.beginPath();
              ctx.arc(node.x, node.y, size, 0, 2 * Math.PI);
              ctx.fillStyle = node.color;
              ctx.fill();
              ctx.strokeStyle = '#1e293b';
              ctx.lineWidth = 1;
              ctx.stroke();
              ctx.font = `${Math.max(8, 10/globalScale)}px Sans-Serif`;
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillStyle = '#1e293b';
              ctx.fillText(node.label, node.x, node.y + size + 10);
            }}
            linkCanvasObjectMode={() => 'after'}
            linkCanvasObject={(link, ctx) => {
              if (link.type === 'directa' && link.cantidad) {
                const midX = (link.source.x + link.target.x) / 2;
                const midY = (link.source.y + link.target.y) / 2;
                ctx.font = '9px Sans-Serif';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = '#dc2626';
                ctx.fillText(`${link.cantidad} txs`, midX, midY);
              }
            }}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-slate-400">
            <div className="text-center">
              <Network size={48} className="mx-auto mb-2 opacity-30" />
              <p>No hay relaciones para visualizar</p>
            </div>
          </div>
        )}
      </div>
      <div className="flex gap-4 mt-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
          <span className="text-slate-600">Cliente Investigado</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
          <span className="text-slate-600">Contraparte Común</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-6 h-0.5 bg-red-500"></div>
          <span className="text-slate-600">Tx. Directa</span>
        </div>
      </div>
    </Card>

    {/* Transacciones directas */}
    {relaciones.transacciones_directas?.length > 0 && (
      <Card className="p-4 bg-white">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <AlertTriangle size={16} className="text-red-600" />Transacciones Directas entre Clientes
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 px-3">Origen</th>
                <th className="text-left py-2 px-3">Destino</th>
                <th className="text-right py-2 px-3">Cantidad</th>
                <th className="text-right py-2 px-3">Volumen</th>
                <th className="text-center py-2 px-3">Período</th>
              </tr>
            </thead>
            <tbody>
              {relaciones.transacciones_directas.map((tx, idx) => (
                <tr key={idx} className="border-b hover:bg-slate-50">
                  <td className="py-2 px-3 font-mono text-xs">
                    {tx.origen.substring(0, 8)}...{tx.origen.substring(tx.origen.length - 4)}
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">
                    {tx.destino.substring(0, 8)}...{tx.destino.substring(tx.destino.length - 4)}
                  </td>
                  <td className="py-2 px-3 text-right font-medium">{tx.cantidad}</td>
                  <td className="py-2 px-3 text-right font-bold text-red-600">{formatMoney(tx.volumen)}</td>
                  <td className="py-2 px-3 text-center text-xs text-slate-500">
                    {formatDate(tx.primera_tx)} - {formatDate(tx.ultima_tx)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    )}

    {/* Contrapartes comunes */}
    {relaciones.contrapartes_comunes?.length > 0 && (
      <Card className="p-4 bg-white">
        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <Link2 size={16} className="text-amber-600" />Contrapartes en Común
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {relaciones.contrapartes_comunes.slice(0, 10).map((cp, idx) => (
            <div key={idx} className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="font-mono text-xs text-amber-800 truncate">{cp.cuenta}</p>
              <div className="flex justify-between mt-2 text-sm">
                <span className="text-slate-600">Conecta {cp.clientes_conectados?.length || 0} clientes</span>
                <span className="font-bold text-amber-700">{formatMoney(cp.volumen_total)}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">{cp.total_transacciones} transacciones</p>
            </div>
          ))}
        </div>
      </Card>
    )}

    {/* Patrones de red */}
    {relaciones.patrones_red?.length > 0 && (
      <Card className="p-4 bg-red-50 border-red-200">
        <h3 className="text-sm font-semibold text-red-700 mb-3 flex items-center gap-2">
          <AlertCircle size={16} />Patrones de Red Sospechosos
        </h3>
        <div className="space-y-2">
          {relaciones.patrones_red.map((pattern, idx) => (
            <div key={idx} className="p-3 bg-white rounded-lg border border-red-200">
              <Badge className="bg-red-100 text-red-700 mb-2">{pattern.tipo}</Badge>
              <p className="text-sm text-slate-700">{pattern.descripcion}</p>
            </div>
          ))}
        </div>
      </Card>
    )}
  </div>
);

export default InvestigacionRelacionesTab;
